// Topology rules shared by the explorer and the 3D view
// (docs/adr/2026-09-28-3d-topology.md): health rollups, which nodes are
// entities at each level, where a link is drawn, and the Lock View path.
import type { Link, Monitor, MonitorState, MonitorStatus, Node, NodeType } from "@/api/generated";
import { rollup } from "@/lib/status";

export type Health = { status: MonitorStatus; count: number };

export type TopologyHealth = {
  nodeHealth: Map<string, Health>;
  linkHealth: Map<string, Health>;
  kids: Map<string, string[]>;
};

export function childrenOf(nodes: Map<string, Node>): Map<string, string[]> {
  const kids = new Map<string, string[]>();
  for (const n of nodes.values()) {
    if (n.parent_id) kids.set(n.parent_id, [...(kids.get(n.parent_id) ?? []), n.id]);
  }
  return kids;
}

/** A node's health is the rollup of every monitor in its subtree; a link's, of its own monitors. */
export function computeHealth(nodes: Map<string, Node>, monitors: Monitor[], states: Map<string, MonitorState>): TopologyHealth {
  const direct = new Map<string, MonitorStatus[]>();
  const byLink = new Map<string, MonitorStatus[]>();
  for (const m of monitors) {
    const s = states.get(m.id)?.status ?? "UNKNOWN";
    if (m.node_id) direct.set(m.node_id, [...(direct.get(m.node_id) ?? []), s]);
    if (m.link_id) byLink.set(m.link_id, [...(byLink.get(m.link_id) ?? []), s]);
  }
  const kids = childrenOf(nodes);
  const nodeHealth = new Map<string, Health>();
  const visit = (id: string, seen: Set<string>): MonitorStatus[] => {
    if (seen.has(id)) return [];
    seen.add(id);
    const all = [...(direct.get(id) ?? [])];
    for (const c of kids.get(id) ?? []) all.push(...visit(c, seen));
    nodeHealth.set(id, { status: rollup(all), count: all.length });
    return all;
  };
  for (const n of nodes.values()) if (!n.parent_id || !nodes.has(n.parent_id)) visit(n.id, new Set());
  const linkHealth = new Map<string, Health>();
  for (const [id, list] of byLink) linkHealth.set(id, { status: rollup(list), count: list.length });
  return { nodeHealth, linkHealth, kids };
}

// --- levels -------------------------------------------------------------------

export type Level = "global" | "metro" | "dc" | "hall";

export const LEVELS: Level[] = ["global", "metro", "dc", "hall"];

export const LEVEL_LABEL: Record<Level, string> = { global: "Global", metro: "Metro", dc: "Datacenter", hall: "Hall" };

const SITE: NodeType[] = ["POP", "DATACENTER"];
const ROOM: NodeType[] = ["HALL", "MMR"];

/** The level a node of this type is the focus of (null: never a focus). */
export function focusLevel(type: NodeType): Level | null {
  if (type === "CITY") return "metro";
  if (SITE.includes(type)) return "dc";
  if (ROOM.includes(type)) return "hall";
  return null;
}

/** Root-first ids of a node's ancestry, the node included (cycle-safe). */
export function ancestry(nodes: Map<string, Node>, id: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = nodes.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur.id);
    cur = cur.parent_id ? nodes.get(cur.parent_id) : undefined;
  }
  return out;
}

/**
 * The entities shown at a level. Global: cities, plus PoPs and datacenters
 * that sit in no city. Metro: the outermost PoPs/datacenters in the city.
 * Datacenter: the outermost halls/MMRs, plus racks in no hall. Hall: racks.
 */
export function entitiesAt(level: Level, focus: string | null, nodes: Map<string, Node>, kids: Map<string, string[]>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (id: string, isEntity: (n: Node) => boolean, stop: (n: Node) => boolean) => {
    if (seen.has(id)) return;
    seen.add(id);
    for (const c of kids.get(id) ?? []) {
      const n = nodes.get(c);
      if (!n) continue;
      if (isEntity(n)) out.push(n.id);
      else if (!stop(n)) walk(n.id, isEntity, stop);
    }
  };
  switch (level) {
    case "global": {
      const isSite = (n: Node) => n.type === "CITY" || SITE.includes(n.type);
      const walkRoot = (n: Node) => {
        if (seen.has(n.id)) return;
        seen.add(n.id);
        if (isSite(n)) out.push(n.id);
        else if (n.type === "REGION") for (const c of kids.get(n.id) ?? []) { const cn = nodes.get(c); if (cn) walkRoot(cn); }
      };
      for (const n of nodes.values()) if (!n.parent_id || !nodes.has(n.parent_id)) walkRoot(n);
      break;
    }
    case "metro":
      if (focus) walk(focus, (n) => SITE.includes(n.type), () => false);
      break;
    case "dc":
      if (focus) walk(focus, (n) => ROOM.includes(n.type) || n.type === "RACK", (n) => n.type === "DEVICE");
      break;
    case "hall":
      if (focus) walk(focus, (n) => n.type === "RACK", (n) => n.type === "DEVICE");
      break;
  }
  return out;
}

/** The entity (in `entities`) a node belongs to: itself or its nearest ancestor. */
export function anchorOf(nodes: Map<string, Node>, id: string, entities: Set<string>): string | null {
  const seen = new Set<string>();
  let cur = nodes.get(id);
  while (cur && !seen.has(cur.id)) {
    if (entities.has(cur.id)) return cur.id;
    seen.add(cur.id);
    cur = cur.parent_id ? nodes.get(cur.parent_id) : undefined;
  }
  return null;
}

export type DrawnLink = { link: Link; a: string; z: string };

/** Links whose ends fall in two different entities of this level. */
export function linksAt(links: Link[], nodes: Map<string, Node>, entities: string[]): DrawnLink[] {
  const set = new Set(entities);
  const out: DrawnLink[] = [];
  for (const l of links) {
    const a = anchorOf(nodes, l.a_node_id, set);
    const z = anchorOf(nodes, l.z_node_id, set);
    if (a && z && a !== z) out.push({ link: l, a, z });
  }
  return out;
}

/** The deepest level at which a link is drawn, with its focus. */
export function levelForLink(link: Link, nodes: Map<string, Node>, kids: Map<string, string[]>): { level: Level; focus: string | null } {
  const upA = ancestry(nodes, link.a_node_id);
  const upZ = ancestry(nodes, link.z_node_id);
  const shared = upA.filter((id) => upZ.includes(id));
  for (const id of [...shared].reverse()) {
    const n = nodes.get(id);
    const level = n ? focusLevel(n.type) : null;
    if (n && level && linksAt([link], nodes, entitiesAt(level, n.id, nodes, kids)).length > 0) return { level, focus: n.id };
  }
  return { level: "global", focus: null };
}

// --- Lock View paths -------------------------------------------------------------

/** Root-first slugs of a node. */
export function slugPath(nodes: Map<string, Node>, id: string): string[] {
  return ancestry(nodes, id).map((x) => nodes.get(x)!.slug);
}

function endsWith(path: string[], suffix: string[]): boolean {
  if (suffix.length > path.length) return false;
  return suffix.every((s, i) => path[path.length - suffix.length + i] === s);
}

/** The shortest trailing run of slugs that names exactly this node. */
export function shortPath(nodes: Map<string, Node>, id: string): string {
  const path = slugPath(nodes, id);
  const all = [...nodes.keys()].map((n) => slugPath(nodes, n));
  for (let k = 1; k <= path.length; k++) {
    const suffix = path.slice(-k);
    if (all.filter((p) => endsWith(p, suffix)).length === 1) return suffix.join("/");
  }
  return path.join("/");
}

/** Resolve a slug path (full or a unique trailing run). */
export function resolvePath(nodes: Map<string, Node>, path: string): { id: string } | { error: string } {
  const suffix = path.split("/").filter(Boolean);
  if (suffix.length === 0) return { error: "empty path" };
  const hits = [...nodes.keys()].filter((id) => endsWith(slugPath(nodes, id), suffix));
  if (hits.length === 1) return { id: hits[0] };
  return { error: hits.length === 0 ? `nothing is called "${path}"` : `"${path}" matches ${hits.length} places; use a longer path` };
}

export type ViewAt = { level: Level; focus: string | null; selected: string | null };

/** "<level>:<path>" for a view (the PRD's example: dc:cgk-dc3/hall-core). */
export function encodeView(nodes: Map<string, Node>, view: ViewAt): string {
  const target = view.selected ?? view.focus;
  return target ? `${view.level}:${shortPath(nodes, target)}` : view.level;
}

/**
 * Parse "<level>:<path>". The path may name the focus itself or anything
 * inside it, which is then selected. For "global:<path>" the site holding it
 * is selected.
 */
export function decodeView(nodes: Map<string, Node>, kids: Map<string, string[]>, raw: string): ViewAt | { error: string } {
  const i = raw.indexOf(":");
  const level = (i < 0 ? raw : raw.slice(0, i)) as Level;
  const path = i < 0 ? "" : raw.slice(i + 1);
  if (!LEVELS.includes(level)) return { error: `unknown level "${level}"` };
  if (!path) return level === "global" ? { level, focus: null, selected: null } : { error: `${level} needs a path` };
  const hit = resolvePath(nodes, path);
  if ("error" in hit) return hit;
  const chain = ancestry(nodes, hit.id);
  if (level === "global") {
    const sites = new Set(entitiesAt("global", null, nodes, kids));
    return { level, focus: null, selected: anchorOf(nodes, hit.id, sites) };
  }
  const focus = [...chain].reverse().find((id) => focusLevel(nodes.get(id)!.type) === level);
  if (!focus) return { error: `"${path}" is not inside a ${LEVEL_LABEL[level].toLowerCase()}` };
  return { level, focus, selected: hit.id === focus ? null : hit.id };
}

/** Where the 3D view should open for a node picked in the palette. */
export function viewForNode(nodes: Map<string, Node>, id: string): ViewAt {
  const chain = ancestry(nodes, id);
  for (const x of [...chain].reverse()) {
    const lvl = focusLevel(nodes.get(x)!.type);
    if (lvl) return { level: lvl, focus: x, selected: x === id ? null : id };
  }
  return { level: "global", focus: null, selected: null };
}
